import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../../../../components";
import { SectionCard } from "../../../../../components/section/SectionCard";
import { CompactMemberCard } from "../../../members/components/CompactMemberCard";
import TooltipContainer from "../TooltipContainer";
import type { UserProfile } from "../../../../../client";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  createUploadResumeOptions,
  getLeadsOptions,
  getOrganizationOptions,
  useUploadResume,
} from "../../../../../services";
import { toast } from "sonner";
import { useAddOrganizationProfileV2 } from "../../../../../services/organtization/add-organization-profile";
import { useGetMulitpleBackgroundJobs } from "../../../../../services/jobs/get-multiple-jobs";
import { motion, AnimatePresence } from "framer-motion";
import { organizationProfileLeadQueryOptions } from "../../../../../services/organtization/lead/get-profile-leads";
import { queryClient } from "../../../../../utils/query-client";

interface WeeklySectionCardProps {
  organizationId: string;
  profiles?: UserProfile[];
  newWeeklyProfiles?: number;
  newWeeklyProfileLeads?: number;
  newWeeklyLeads?: number;
  newWeeklyProfileVersions?: number;
}

export const WeeklySectionCard: React.FC<WeeklySectionCardProps> = ({
  organizationId,
  profiles = [],
  newWeeklyProfiles = 0,
  newWeeklyProfileLeads = 0,
  newWeeklyLeads = 0,
  newWeeklyProfileVersions = 0,
}) => {
  const maxSlots = 5;
  const safeProfiles = profiles || [];

  const displayedProfiles = useMemo(() => {
    let displayedSafeProfiles = [...safeProfiles]
      .sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      })
      .slice(0, maxSlots);

    return displayedSafeProfiles;
  }, [safeProfiles, maxSlots]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadResume = useUploadResume();

  const [fileQueue, setFileQueue] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalFiles, setTotalFiles] = useState(0);

  const [shouldPoll, setShouldPoll] = useState(false);
  const [pendingProfileIds, setPendingProfileIds] = useState<string[]>([]);
  const [localWeeklyProfiles, setLocalWeeklyProfiles] = useState(0);
  const [localWeeklyProfileLeads, setLocalWeeklyProfileLeads] = useState(0);

  const profileIds = profiles?.map((p) => p.profileId);

  const invalidateOrgCache = (profileId: string) => {
    const getLinkQueryKey = (orgId: string) =>
      getOrganizationOptions(orgId).queryKey;

    const otherQuery = (organizationId: string, profileId: string) =>
      organizationProfileLeadQueryOptions(organizationId, profileId).queryKey;

    const getLeadsQueryKey = (orgId: string) => getLeadsOptions(orgId).queryKey;

    queryClient.invalidateQueries({
      queryKey: getLinkQueryKey(organizationId),
    });
    queryClient.invalidateQueries({
      queryKey: otherQuery(organizationId, profileId),
    });
    queryClient.invalidateQueries({
      queryKey: getLeadsQueryKey(organizationId),
    });
  };

  const {
    data: activeBackgroundJobs,
    isSuccess,
    isError,
    error,
    fetchStatus,
  } = useGetMulitpleBackgroundJobs(
    profiles?.map((p) => p.profileId),
    "resume_parser",
    shouldPoll && profiles && profiles.length > 0 ? true : false,
    {
      refetchInterval: shouldPoll ? 2000 : false,
    },
  );

  // Stop polling when activeBackgroundJobs becomes empty
  useEffect(() => {
    if (activeBackgroundJobs && activeBackgroundJobs.length === 0) {
      setShouldPoll(false);
      setPendingProfileIds([]);
    } else if (activeBackgroundJobs == null) {
      if (pendingProfileIds.length > 0) {
        invalidateOrgCache(pendingProfileIds[0]);
        setShouldPoll(false);
        setPendingProfileIds([]);
      }
    } else {
      const profileIdsWithActiveBackgroundJobs = activeBackgroundJobs
        .map((job) => job.profileId ?? "")
        .filter((id) => id !== "");

      setShouldPoll(true);
      setPendingProfileIds(profileIdsWithActiveBackgroundJobs);
    }
  }, [activeBackgroundJobs]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";

    if (files.length === 0) return;

    const pdfs = files.filter((f) => f.type.includes("pdf"));
    const rejected = files.filter((f) => !f.type.includes("pdf"));
    if (rejected.length > 0) {
      toast.error(`Skipped non-PDF files: ${rejected.map((f) => f.name).join(", ")}`);
    }

    if (pdfs.length === 0) return;

    setTotalFiles((prev) => prev + pdfs.length);
    setFileQueue((prev) => [...prev, ...pdfs]);
  };

  const weeklyProfilesCount: number = useMemo(() => {
    if (pendingProfileIds.length > 0) {
      return displayedProfiles.some((p) => p.profileId === pendingProfileIds[0])
        ? newWeeklyProfiles
        : newWeeklyProfiles + 1;
    }

    return newWeeklyProfiles;
  }, [displayedProfiles, newWeeklyProfiles, pendingProfileIds]);

  const addOrganizationProfileV2 = useAddOrganizationProfileV2();

  // Reset totalFiles counter once queue is fully drained
  useEffect(() => {
    if (fileQueue.length === 0 && !isProcessing && totalFiles > 0) {
      setTotalFiles(0);
    }
  }, [fileQueue, isProcessing]);

  useEffect(() => {
    if (fileQueue.length === 0 || isProcessing) return;
    setIsProcessing(true);
    const nextFile = fileQueue[0];

    addOrganizationProfileV2.mutate(
      { path: { organizationID: organizationId } },
      {
        onSuccess: (memberResponseBody) => {
          const newProfileId = memberResponseBody.profileId ?? "";
          if (newProfileId) {
            setPendingProfileIds((prev) => [...prev, newProfileId]);
            setLocalWeeklyProfiles(1);
          }

          uploadResume.mutate(
            createUploadResumeOptions(newProfileId, organizationId ?? "", nextFile),
            {
              onSuccess: () => {
                setLocalWeeklyProfiles(0);
                setFileQueue((prev) => prev.slice(1));
                setIsProcessing(false);
                setTimeout(() => setShouldPoll(true), 2000);
              },
              onError: () => {
                toast.error(`Failed to upload ${nextFile.name}. Skipping.`);
                setFileQueue((prev) => prev.slice(1));
                setIsProcessing(false);
              },
            },
          );
        },
        onError: (error) => {
          console.error(error);
          toast.error(`Failed to create profile for ${nextFile.name}. Skipping.`);
          setFileQueue((prev) => prev.slice(1));
          setIsProcessing(false);
        },
      },
    );
  }, [fileQueue, isProcessing]);
  return (
    <SectionCard title="Weekly stats" wrapperClassName="w-full lg:w-2/3">
      <div className="flex flex-row gap-2">
        <div className="border border-dashed border-gray-4 flex-1 h-8 rounded-xl flex items-center justify-between p-5">
          <h4 className="font-bold text-sm">Profiles</h4>
          <span className="font-bold text-red-3">{weeklyProfilesCount}</span>
        </div>
        <div className="border border-dashed border-gray-4 flex-1 h-8 rounded-xl flex items-center justify-between p-5">
          <h4 className="font-bold text-sm">Leads</h4>
          <span className="font-bold text-red-3">{newWeeklyLeads}</span>
        </div>
        <div className="border border-dashed border-gray-4 flex-1 h-8 rounded-xl flex items-center justify-between p-5">
          <h4 className="font-bold text-sm">Profile Versions</h4>
          <span className="font-bold text-red-3">{newWeeklyProfileVersions}</span>
        </div>
      </div>
      {(fileQueue.length > 0 || isProcessing) && (
        <p className="text-sm text-gray-500">
          Importing {totalFiles - fileQueue.length + (isProcessing ? 1 : 0)} of {totalFiles} files...
        </p>
      )}
      <div className="flex flex-row gap-3 overflow-y-scroll">
        <AnimatePresence mode="popLayout">
          {pendingProfileIds.length === 0 ||
          displayedProfiles.some(
            (p) => p.profileId === pendingProfileIds[0],
          ) ? (
            <motion.div
              key={`empty`}
              className={`h-full min-h-48 rounded-3xl cursor-pointer${fileQueue.length > 0 || isProcessing ? " pointer-events-none opacity-50" : ""}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{
                duration: 0.3,
                delay: displayedProfiles.length * 0.05,
              }}
              layout
            >
              <TooltipContainer
                tooltipText="Import new Profile"
                side="top"
                sideOffset={-60}
              >
                <div
                  className="rounded-3xl min-w-48 h-48 flex items-center justify-center group border border-gray-1  hover:border-2 hover:border-gray-2"
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCirclePlus}
                    className="text-red-3 w-5 h-5 group-hover:scale-125 transition-transform duration-200"
                  />
                </div>
              </TooltipContainer>
            </motion.div>
          ) : (
            <motion.div
              key={"pending"}
              initial={{ opacity: 0, scale: 0.8, x: -20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.8, x: 20 }}
              transition={{
                duration: 0.3,
                delay: 0.01,
                ease: "easeOut",
              }}
              layout
            >
              <Link
                to={`/dash/${organizationId}/members/${pendingProfileIds[0]}`}
              >
                <CompactMemberCard
                  key={pendingProfileIds[0]}
                  isImporting={true}
                  profileId={pendingProfileIds[0]}
                  profileData={{}}
                  isManager={false}
                />
              </Link>
            </motion.div>
          )}
          {displayedProfiles.slice(0, 4).map((profile, index) => (
            <motion.div
              key={profile.profileId}
              initial={{ opacity: 0, scale: 0.8, x: -20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.8, x: 20 }}
              transition={{
                duration: 0.3,
                delay: index * 0.05,
                ease: "easeOut",
              }}
              layout
            >
              <Link to={`/dash/${organizationId}/members/${profile.profileId}`}>
                <CompactMemberCard
                  key={profile.profileId}
                  isImporting={
                    pendingProfileIds.filter((id) => id === profile.profileId)
                      .length > 0
                  }
                  {...profile}
                />
              </Link>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        multiple
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
    </SectionCard>
  );
};
